import { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Globe, RefreshCw, Copy, Phone, Eye, Search, Smartphone, Zap, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  new: { label: "New", cls: "bg-sky-100 text-sky-800 hover:bg-sky-100" },
  called: { label: "Called", cls: "bg-amber-100 text-amber-800 hover:bg-amber-100" },
  accepted: { label: "Accepted", cls: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" },
  packing: { label: "Packing", cls: "bg-indigo-100 text-indigo-800 hover:bg-indigo-100" },
  on_the_way: { label: "On the Way", cls: "bg-orange-100 text-orange-800 hover:bg-orange-100" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700 hover:bg-red-100" },
  delivered: { label: "Delivered", cls: "bg-slate-200 text-slate-700 hover:bg-slate-200" },
};

const SOURCE = {
  website: { label: "Website", cls: "bg-sky-100 text-sky-800 hover:bg-sky-100 border-sky-200", Icon: Globe },
  app: { label: "Mobile App", cls: "bg-violet-100 text-violet-800 hover:bg-violet-100 border-violet-200", Icon: Smartphone },
  blinkit: { label: "Blinkit", cls: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-200", Icon: Zap },
};

export default function OnlineOrders() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [fSource, setFSource] = useState("all");
  const [month, setMonth] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => {
    const params = {};
    if (filter !== "all") params.status = filter;
    if (fSource !== "all") params.source = fSource;
    if (month) params.month = month;
    if (search.trim()) params.q = search.trim();
    api.get("/online-orders", { params }).then(({ data }) => setItems(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, month, fSource]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const createBill = async (o) => {
    try {
      const { data } = await api.post(`/online-orders/${o.id}/create-invoice`);
      toast.success(`Invoice ${data.invoice_no} ban gaya — Invoices page par print karein`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bill nahi ban paya");
    }
  };

  const setStatus = async (o, status) => {
    try {
      await api.put(`/online-orders/${o.id}`, { status });
      toast.success(`Order ${o.order_no} → ${STATUS[status]?.label || status}`);
      load();
      if (detail?.id === o.id) setDetail(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const callCustomer = (o) => {
    window.open(`tel:${o.phone}`);
    if (o.status === "new") setStatus(o, "called");
  };

  const viewRx = async (o) => {
    try {
      const res = await api.get(`/online-orders/${o.id}/prescription`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Prescription load nahi hui");
    }
  };

  const copyEndpoint = () => {
    navigator.clipboard.writeText(`${API_BASE}/online-orders`);
    toast.success("Endpoint copied");
  };

  return (
    <div className="space-y-5" data-testid="online-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <Globe className="w-6 h-6 text-emerald-700" /> Online Orders
          </h1>
          <p className="text-sm text-slate-500">Website orders yahan aate hain. Accept karne se pehle customer ko <span className="font-semibold text-amber-700">Call karke verify</span> karein.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Order no / name / phone" data-testid="orders-search" className="pl-8 h-10 w-52 bg-white" />
          </div>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="orders-month-filter" className="h-10 w-40 bg-white" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="orders-filter" className="h-10 w-36 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="called">Called</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="packing">Packing</SelectItem>
              <SelectItem value="on_the_way">On the Way</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fSource} onValueChange={setFSource}>
            <SelectTrigger data-testid="orders-source-filter" className="h-10 w-36 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="website">Website</SelectItem>
              <SelectItem value="app">Mobile App</SelectItem>
              <SelectItem value="blinkit">Blinkit</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="orders-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <Card className="p-4 border-emerald-200 bg-emerald-50/50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="text-xs text-emerald-900">
            <span className="font-semibold">Website integration:</span> POST from your website to{" "}
            <code className="bg-white px-1.5 py-0.5 rounded font-mono-num">{API_BASE}/online-orders</code>{" "}
            body: <code className="bg-white px-1.5 py-0.5 rounded font-mono-num">{'{"customer_name","phone","address","items":[{"medicine_name","quantity"}],"source":"website|app|blinkit"}'}</code>
          </div>
          <Button size="sm" variant="outline" onClick={copyEndpoint} data-testid="copy-endpoint-btn" className="shrink-0"><Copy className="w-3.5 h-3.5 mr-1" /> Copy URL</Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {items.map((o) => (
          <Card key={o.id} className="p-4 border-slate-200" data-testid={`order-card-${o.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-mono-num font-bold text-slate-900">{o.order_no}</div>
                <div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("en-IN")}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {(() => { const S = SOURCE[o.source] || SOURCE.website; return (
                  <Badge className={`${S.cls} border text-[10px]`} data-testid={`order-source-${o.id}`}><S.Icon className="w-3 h-3 mr-1" />{S.label}</Badge>
                ); })()}
                <Badge className={STATUS[o.status]?.cls} data-testid={`order-status-${o.id}`}>{STATUS[o.status]?.label || o.status}</Badge>
                {o.prescription_path && <Badge className="bg-purple-100 text-purple-700 border-purple-200 border text-[10px]" data-testid={`order-rx-badge-${o.id}`}>Rx</Badge>}
                <Badge className={`border text-[10px] ${o.payment_method === "upi" ? "bg-indigo-100 text-indigo-700 border-indigo-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}`} data-testid={`order-pay-${o.id}`}>{o.payment_method === "upi" ? "UPI" : "COD"}</Badge>
              </div>
            </div>
            <div className="mt-3 text-sm">
              <div className="font-semibold">{o.customer_name} <span className="font-mono-num font-normal text-slate-500">• {o.phone}</span></div>
              {o.email && <div className="text-xs text-slate-400 mt-0.5">{o.email}</div>}
              {o.address && <div className="text-xs text-slate-500 mt-0.5">{o.address}</div>}
              <div className="mt-2 space-y-1">
                {o.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-xs bg-slate-50 rounded px-2 py-1.5">
                    <span>{it.medicine_name}</span>
                    <span className="font-mono-num font-semibold">× {it.quantity}</span>
                  </div>
                ))}
              </div>
              {o.called_at && <div className="text-xs text-amber-700 mt-2">Called by {o.called_by} • {new Date(o.called_at).toLocaleString("en-IN")}</div>}
              {o.note && <div className="text-xs text-slate-500 mt-2 italic">"{o.note}"</div>}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {["new", "called", "accepted"].includes(o.status) && (
                <Button size="sm" variant="outline" data-testid={`order-call-${o.id}`} onClick={() => callCustomer(o)} className="h-8 text-amber-700 border-amber-300 hover:bg-amber-50">
                  <Phone className="w-3.5 h-3.5 mr-1" /> Call
                </Button>
              )}
              {["new", "called"].includes(o.status) && (
                <>
                  <Button size="sm" data-testid={`order-accept-${o.id}`} onClick={() => setStatus(o, "accepted")} className="h-8 bg-emerald-900 hover:bg-emerald-800">Accept</Button>
                  <Button size="sm" variant="outline" data-testid={`order-reject-${o.id}`} onClick={() => setStatus(o, "rejected")} className="h-8 text-red-600 border-red-200">Reject</Button>
                </>
              )}
              {o.status === "accepted" && (
                <Button size="sm" data-testid={`order-packing-${o.id}`} onClick={() => setStatus(o, "packing")} className="h-8 bg-indigo-600 hover:bg-indigo-700">Start Packing</Button>
              )}
              {o.status === "packing" && (
                <Button size="sm" data-testid={`order-otw-${o.id}`} onClick={() => setStatus(o, "on_the_way")} className="h-8 bg-orange-500 hover:bg-orange-600">Out for Delivery</Button>
              )}
              {o.status === "on_the_way" && (
                <Button size="sm" data-testid={`order-deliver-${o.id}`} onClick={() => setStatus(o, "delivered")} className="h-8 bg-emerald-900 hover:bg-emerald-800">Mark Delivered</Button>
              )}
              {["accepted", "packing", "on_the_way", "delivered"].includes(o.status) && !o.invoice_no && (
                <Button size="sm" variant="outline" data-testid={`order-bill-${o.id}`} onClick={() => createBill(o)} className="h-8 text-emerald-700 border-emerald-300 hover:bg-emerald-50">
                  <FileText className="w-3.5 h-3.5 mr-1" /> Bill Banao
                </Button>
              )}
              {o.invoice_no && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] self-center" data-testid={`order-billed-${o.id}`}>Bill: {o.invoice_no}</Badge>}
              <Button size="sm" variant="ghost" data-testid={`order-open-${o.id}`} onClick={() => setDetail(o)} className="h-8 ml-auto text-slate-600">
                <Eye className="w-3.5 h-3.5 mr-1" /> Open
              </Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="p-10 border-slate-200 text-center lg:col-span-2">
            <Globe className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No online orders found. Filters badal kar dekhein ya website connect karein.</p>
          </Card>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {detail?.order_no}
              {detail && <Badge className={STATUS[detail.status]?.cls} data-testid="order-detail-status">{STATUS[detail.status]?.label || detail.status}</Badge>}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="text-sm text-slate-600 space-y-1.5">
                <div className="font-semibold text-slate-900">{detail.customer_name}</div>
                <div className="flex items-center justify-between">
                  <span className="font-mono-num">{detail.phone}</span>
                  <Button size="sm" variant="outline" data-testid="order-detail-call" onClick={() => callCustomer(detail)} className="h-7 text-amber-700 border-amber-300">
                    <Phone className="w-3 h-3 mr-1" /> Call
                  </Button>
                </div>
                {detail.address && <div className="text-xs">{detail.address}</div>}
                <div className="text-xs text-slate-400">Placed: {new Date(detail.created_at).toLocaleString("en-IN")}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1.5">Source: {(() => { const S = SOURCE[detail.source] || SOURCE.website; return <Badge className={`${S.cls} border text-[10px]`} data-testid="order-detail-source"><S.Icon className="w-3 h-3 mr-1" />{S.label}</Badge>; })()}</div>
                {detail.note && <div className="text-xs italic">"{detail.note}"</div>}
                {detail.prescription_path && (
                  <button onClick={() => viewRx(detail)} data-testid="order-rx-view" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-3 py-1.5 rounded-full hover:bg-purple-100">
                    <FileText className="w-3.5 h-3.5" /> Prescription photo dekhein
                  </button>
                )}
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Medicines ordered</div>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {detail.items.map((it, i) => (
                    <div key={i} className="px-3 py-2 flex justify-between text-sm" data-testid={`order-detail-item-${i}`}>
                      <span>{it.medicine_name}</span>
                      <span className="font-mono-num font-semibold">× {it.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>

              {(detail.history || []).length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Timeline</div>
                  <div className="space-y-1.5">
                    {detail.history.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs" data-testid={`order-history-${i}`}>
                        <Badge className={`${STATUS[h.status]?.cls || "bg-slate-100 text-slate-600"} text-[10px]`}>{STATUS[h.status]?.label || h.status}</Badge>
                        <span className="text-slate-500">{new Date(h.at).toLocaleString("en-IN")}</span>
                        <span className="text-slate-400">by {h.by}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="order-detail-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
